import {
  getAdjacentCaseSidebarUrlSection,
  getCaseSidebarNavigationLabel,
  isNestedFormSection,
  resolveCaseSidebarItemId,
  resolveLegacyFormSection,
} from 'components/CaseSidebar';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import type { ComponentType } from 'react';
import { lazy, useEffect, useState } from 'react';
import { Financing } from './FinancingAndProperty/Financing';
import { Property } from './FinancingAndProperty/Property';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { BtlApplication } from '../../api/btlApplicationForm/types';
import { useIsBtlCaseSidebarEnabled } from 'providers/PosthogProvider';
import { TypedFormatMessageFn } from '../../types/intl';
import {
  getAdditionalInformationStepIndex,
  getDocumentsStepIndex,
  getNextStep,
  getPostOfferStepIndex,
  getPreviousStep,
  getValuationStepIndex,
  hasPostOfferStep,
  hasValuationStep,
  isApplicationSubmittedOrLater,
  isDipAcceptedOrLater,
} from './constants';
import { BtlApplicationFormPageEnum, BtlFormRoute, BtlFormRoutes, RequiredFormKeys } from './types';

const isEditable = (data?: BtlApplication) => data?.status === 'NEW' || data?.status === 'DIP_ACCEPTED' || !data;

const CASE_SIDEBAR_FORM_PAGES: Partial<Record<CaseSidebarItemId, ComponentType<any>>> = {
  [CaseSidebarItemId.FINANCING]: Financing,
  [CaseSidebarItemId.PROPERTY]: Property,
};

const btlFormNavigationMap = (formatMessage: TypedFormatMessageFn, data?: BtlApplication): BtlFormRoutes => ({
  [BtlApplicationFormPageEnum.CASE_SUMMARY]: {
    FormPage: lazy(() => import('./CaseSummary/CaseSummary')),
    activeStepIndex: () => undefined,
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY]: {
    FormPage: lazy(() => import('./FinancingAndProperty/FinancingAndProperty')),
    activeStepIndex: () => 0,
    previousSection: BtlApplicationFormPageEnum.CASE_SUMMARY,
    previousLabel: formatMessage({ id: 'btlApplicationForm.FINANCING_AND_PROPERTY.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.APPLICANTS,
    nextLabel: formatMessage({ id: 'btlApplicationForm.FINANCING_AND_PROPERTY.nextLabel' }),
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.APPLICANTS]: {
    FormPage: lazy(() => import('./Applicants/Applicants')),
    activeStepIndex: () => 1,
    previousSection: BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY,
    previousLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.PRODUCT,
    nextLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS.nextLabel' }),
    showStandaloneFormFooter: true,
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.APPLICANTS_COMPANY]: {
    FormPage: lazy(() => import('./Applicants/Company/Company')),
    activeStepIndex: () => 1,
    previousSection: BtlApplicationFormPageEnum.APPLICANTS,
    previousLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.APPLICANTS,
    nextLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.nextLabel' }),
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL]: {
    FormPage: lazy(() => import('./Applicants/Individual/Individual')),
    activeStepIndex: () => 1,
    previousSection: BtlApplicationFormPageEnum.APPLICANTS,
    previousLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.APPLICANTS,
    nextLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.nextLabel' }),
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.PRODUCT]: {
    FormPage: lazy(() => import('./Product/ProductSelector')),
    activeStepIndex: () => 2,
    previousSection: BtlApplicationFormPageEnum.APPLICANTS,
    previousLabel: formatMessage({ id: 'btlApplicationForm.PRODUCT.previousLabel' }),
    nextSection: data?.isTopSlicing
      ? BtlApplicationFormPageEnum.TOP_SLICING
      : BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
    nextLabel: formatMessage({
      id: `btlApplicationForm.PRODUCT.nextLabel${data?.isTopSlicing ? '.topSlicingVariant' : ''}`,
    }),
    showStandaloneFormFooter: true,
    isFormEditable: isEditable(data),
  },
  // NOTE: TOP_SLICING is optional, dependent on the uiState.topSlicingEnabled property
  [BtlApplicationFormPageEnum.TOP_SLICING]: {
    FormPage: lazy(() => import('./TopSlicing/TopSlicing')),
    activeStepIndex: () => 3,
    previousSection: BtlApplicationFormPageEnum.PRODUCT,
    previousLabel: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
    nextLabel: formatMessage({ id: 'btlApplicationForm.TOP_SLICING.nextLabel' }),
    showStandaloneFormFooter: true,
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.TOP_SLICING_HOUSEHOLD]: {
    FormPage: lazy(() => import('./TopSlicing/Household/Household')),
    activeStepIndex: () => 3,
    previousSection: BtlApplicationFormPageEnum.TOP_SLICING,
    previousLabel: formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.TOP_SLICING,
    nextLabel: formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.nextLabel' }),
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.AFFORDABILITY_CHECK]: {
    FormPage: lazy(() => import('./common/AffordabilityCheck/AffordabilityCheck')),
    activeStepIndex: () => (data?.isTopSlicing ? 4 : 3),
    previousSection: data?.isTopSlicing ? BtlApplicationFormPageEnum.TOP_SLICING : BtlApplicationFormPageEnum.PRODUCT,
    previousLabel: formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.previousLabel' }),
    nextSection:
      data?.status === 'DIP_ACCEPTED' ? BtlApplicationFormPageEnum.SOLICITORS : BtlApplicationFormPageEnum.SUBMIT_DIP,
    nextLabel: formatMessage({
      id:
        data?.status === 'DIP_ACCEPTED'
          ? 'btlApplicationForm.AFFORDABILITY_CHECK.nextLabelDipAccepted'
          : 'btlApplicationForm.AFFORDABILITY_CHECK.nextLabel',
    }),
    showStandaloneFormFooter: true,
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.SUBMIT_DIP]: {
    FormPage: lazy(() => import('./SubmitDip/SubmitDip')),
    activeStepIndex: () => (data?.isTopSlicing ? 5 : 4),
    previousSection: BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
    previousLabel: formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.CASE_SUMMARY,
    nextLabel: formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.nextLabel' }),
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.SOLICITORS]: {
    FormPage: lazy(() => import('./Solicitors/Solicitors')),
    activeStepIndex: () => (data?.isTopSlicing ? 5 : 4),
    previousSection: BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
    previousLabel: formatMessage({ id: 'btlApplicationForm.SOLICITORS.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.BANK_DETAILS,
    nextLabel: formatMessage({ id: 'btlApplicationForm.SOLICITORS.nextLabel' }),
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.BANK_DETAILS]: {
    FormPage: lazy(() => import('./BankDetails/BankDetails')),
    activeStepIndex: () => (data?.isTopSlicing ? 6 : 5),
    previousSection: BtlApplicationFormPageEnum.SOLICITORS,
    previousLabel: formatMessage({ id: 'btlApplicationForm.BANK_DETAILS.previousLabel' }),
    nextSection:
      getNextStep(BtlApplicationFormPageEnum.BANK_DETAILS, data?.status) ??
      (isApplicationSubmittedOrLater(data?.status)
        ? BtlApplicationFormPageEnum.COMMUNICATION
        : isDipAcceptedOrLater(data?.status)
        ? BtlApplicationFormPageEnum.DOCUMENTS
        : hasValuationStep(data?.status)
        ? BtlApplicationFormPageEnum.VALUATION
        : BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION),
    nextLabel: formatMessage({
      id: (isApplicationSubmittedOrLater(data?.status)
        ? 'btlApplicationForm.BANK_DETAILS.nextLabelCommunication'
        : 'btlApplicationForm.BANK_DETAILS.nextLabel') as any,
    }),
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.COMMUNICATION]: {
    FormPage: lazy(() => import('./Notes/BtlNotes').then(module => ({ default: module.BtlNotes }))),
    activeStepIndex: () => {
      const baseIndex = data?.isTopSlicing ? 7 : 6;
      return baseIndex;
    },
    previousSection:
      getPreviousStep(BtlApplicationFormPageEnum.COMMUNICATION, data?.status) ??
      BtlApplicationFormPageEnum.BANK_DETAILS,
    previousLabel: formatMessage({ id: 'btlApplicationForm.COMMUNICATION.previousLabel' as any }),
    nextSection:
      getNextStep(BtlApplicationFormPageEnum.COMMUNICATION, data?.status) ?? BtlApplicationFormPageEnum.DOCUMENTS,
    nextLabel: formatMessage({
      id: 'btlApplicationForm.COMMUNICATION.nextLabelDocuments' as any,
    }),
    showStandaloneFormFooter: true,
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.DOCUMENTS]: {
    FormPage: lazy(() => import('./Documents/Documents').then(module => ({ default: module.Documents }))),
    activeStepIndex: () => getDocumentsStepIndex(data?.status, data?.isTopSlicing) ?? (data?.isTopSlicing ? 8 : 7),
    previousSection:
      getPreviousStep(BtlApplicationFormPageEnum.DOCUMENTS, data?.status) ?? BtlApplicationFormPageEnum.BANK_DETAILS,
    previousLabel: formatMessage({
      id: (isApplicationSubmittedOrLater(data?.status)
        ? 'btlApplicationForm.DOCUMENTS.previousLabelCommunication'
        : 'btlApplicationForm.DOCUMENTS.previousLabel') as any,
    }),
    nextSection:
      getNextStep(BtlApplicationFormPageEnum.DOCUMENTS, data?.status) ??
      (hasValuationStep(data?.status)
        ? BtlApplicationFormPageEnum.VALUATION
        : BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION),
    nextLabel: formatMessage({ id: 'btlApplicationForm.DOCUMENTS.nextLabel' }),
    showStandaloneFormFooter: true,
    isFormEditable: isEditable(data),
  },
  [BtlApplicationFormPageEnum.VALUATION]: {
    FormPage: lazy(() => import('./Valuation/BtlValuation').then(module => ({ default: module.BtlValuation }))),
    activeStepIndex: () => getValuationStepIndex(data?.status, data?.isTopSlicing) ?? (data?.isTopSlicing ? 9 : 8),
    previousSection:
      getPreviousStep(BtlApplicationFormPageEnum.VALUATION, data?.status) ?? BtlApplicationFormPageEnum.DOCUMENTS,
    previousLabel: formatMessage({ id: 'btlApplicationForm.VALUATION.previousLabel' }),
    nextSection:
      getNextStep(BtlApplicationFormPageEnum.VALUATION, data?.status) ??
      (hasPostOfferStep(data?.status)
        ? BtlApplicationFormPageEnum.POST_OFFER
        : BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION),
    nextLabel: formatMessage({ id: 'btlApplicationForm.VALUATION.nextLabel' }),
    showStandaloneFormFooter: true,
    isFormEditable: false,
  },
  [BtlApplicationFormPageEnum.POST_OFFER]: {
    FormPage: lazy(() => import('./PostOffer/BtlPostOffer').then(module => ({ default: module.BtlPostOffer }))),
    activeStepIndex: () => getPostOfferStepIndex(data?.status, data?.isTopSlicing) ?? (data?.isTopSlicing ? 10 : 9),
    previousSection: BtlApplicationFormPageEnum.VALUATION,
    previousLabel: formatMessage({ id: 'btlApplicationForm.POST_OFFER.previousLabel' }),
    nextSection: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
    nextLabel: formatMessage({ id: 'btlApplicationForm.POST_OFFER.nextLabel' }),
    showStandaloneFormFooter: true,
    isFormEditable: false,
  },
  [BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION]: {
    FormPage: lazy(() => import('./AdditionalInformation/AdditionalInformation')),
    activeStepIndex: () => getAdditionalInformationStepIndex(data?.status, data?.isTopSlicing),
    previousSection: (() => {
      return (
        getPreviousStep(BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION, data?.status) ??
        (hasPostOfferStep(data?.status)
          ? BtlApplicationFormPageEnum.POST_OFFER
          : hasValuationStep(data?.status)
          ? BtlApplicationFormPageEnum.VALUATION
          : isDipAcceptedOrLater(data?.status)
          ? BtlApplicationFormPageEnum.DOCUMENTS
          : isApplicationSubmittedOrLater(data?.status)
          ? BtlApplicationFormPageEnum.COMMUNICATION
          : BtlApplicationFormPageEnum.BANK_DETAILS)
      );
    })(),
    previousLabel: (() => {
      const previousStep =
        getPreviousStep(BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION, data?.status) ??
        (hasPostOfferStep(data?.status)
          ? BtlApplicationFormPageEnum.POST_OFFER
          : hasValuationStep(data?.status)
          ? BtlApplicationFormPageEnum.VALUATION
          : isDipAcceptedOrLater(data?.status)
          ? BtlApplicationFormPageEnum.DOCUMENTS
          : isApplicationSubmittedOrLater(data?.status)
          ? BtlApplicationFormPageEnum.COMMUNICATION
          : BtlApplicationFormPageEnum.BANK_DETAILS);
      const labelKey =
        previousStep === BtlApplicationFormPageEnum.POST_OFFER
          ? 'btlApplicationForm.ADDITIONAL_INFORMATION.previousLabelPostOffer'
          : previousStep === BtlApplicationFormPageEnum.VALUATION
          ? 'btlApplicationForm.ADDITIONAL_INFORMATION.previousLabelValuation'
          : previousStep === BtlApplicationFormPageEnum.DOCUMENTS
          ? 'btlApplicationForm.ADDITIONAL_INFORMATION.previousLabelDocuments'
          : previousStep === BtlApplicationFormPageEnum.COMMUNICATION
          ? 'btlApplicationForm.ADDITIONAL_INFORMATION.previousLabelCommunication'
          : 'btlApplicationForm.ADDITIONAL_INFORMATION.previousLabelBankDetails';
      return formatMessage({ id: labelKey });
    })(),
    nextSection: BtlApplicationFormPageEnum.CASE_SUMMARY,
    nextLabel: formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.nextLabel' }),
    isFormEditable: isEditable(data),
  },
});

export const useBtlFormMappedRoutes = (applicationData?: BtlApplication): BtlFormRoute => {
  const [activeForm, setActiveForm] = useState<BtlFormRoute | null>(null);
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const isCaseSidebarEnabled = useIsBtlCaseSidebarEnabled();
  const formSectionParam = searchParams.get('section');
  const legacyFormSection = resolveLegacyFormSection(formSectionParam, isCaseSidebarEnabled) as
    | BtlApplicationFormPageEnum
    | undefined;
  const navigationMap: BtlFormRoute | null = legacyFormSection
    ? btlFormNavigationMap(formatMessage, applicationData)[legacyFormSection as RequiredFormKeys] ?? null
    : null;

  useEffect(() => {
    const selectedForm = navigationMap;
    if (selectedForm) {
      setActiveForm(selectedForm);
    }
  }, [formSectionParam, searchParams, applicationData]);

  const isNestedFormRoute = isNestedFormSection(formSectionParam);
  const useCaseSidebarStepNavigation =
    isCaseSidebarEnabled && applicationData && formSectionParam && !isNestedFormRoute;

  const caseSidebarNextSection = useCaseSidebarStepNavigation
    ? getAdjacentCaseSidebarUrlSection(formSectionParam, applicationData, 'next')
    : undefined;

  const caseSidebarPreviousSection = useCaseSidebarStepNavigation
    ? getAdjacentCaseSidebarUrlSection(formSectionParam, applicationData, 'previous')
    : undefined;

  const nextSection = useCaseSidebarStepNavigation ? caseSidebarNextSection : activeForm?.nextSection;
  const previousSection = useCaseSidebarStepNavigation ? caseSidebarPreviousSection : activeForm?.previousSection;

  const nextLabel = useCaseSidebarStepNavigation
    ? getCaseSidebarNavigationLabel(formatMessage, formSectionParam, applicationData, 'next') ?? activeForm?.nextLabel
    : activeForm?.nextLabel;

  const previousLabel = useCaseSidebarStepNavigation
    ? getCaseSidebarNavigationLabel(formatMessage, formSectionParam, applicationData, 'previous') ??
      activeForm?.previousLabel
    : activeForm?.previousLabel;

  const caseSidebarItemId = isCaseSidebarEnabled ? resolveCaseSidebarItemId(formSectionParam) : undefined;
  const caseSidebarFormPage = caseSidebarItemId ? CASE_SIDEBAR_FORM_PAGES[caseSidebarItemId] : undefined;

  return {
    ...(activeForm ?? ({} as BtlFormRoute)),
    FormPage: (caseSidebarFormPage ?? activeForm?.FormPage) as BtlFormRoute['FormPage'],
    currentSection: legacyFormSection,
    nextSection,
    previousSection,
    nextLabel,
    previousLabel,
    activeStepIndex: isCaseSidebarEnabled ? () => undefined : activeForm?.activeStepIndex ?? (() => undefined),
  };
};
